import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { Heading, PrimaryButton } from '../../components';
import css from './UnifiedVoucherInput.module.css';

const isNotExpired = voucher => {
  if (!voucher?.expiryDate) {
    return true;
  }
  return new Date(voucher.expiryDate) >= new Date();
};

const UnifiedVoucherInput = props => {
  const {
    intl,
    userVouchers,
    fetchingUserVouchers,
    setGiftCardCode,
    onApplyVoucher,
    onRemoveVoucher,
    disabled,
    giftCardCode,
    currentUser,
    voucherInProgress,
  } = props;

  const activeVouchers =
    userVouchers?.filter(
      voucher => parseFloat(voucher.balance) > 0 && isNotExpired(voucher)
    ) || [];

  const [inputValue, setInputValue] = useState(giftCardCode || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const lastExternalCodeRef = useRef(giftCardCode || '');

  const hasUserVouchers = currentUser && activeVouchers.length > 0;

  const filteredVouchers = activeVouchers.filter(voucher =>
    voucher.code.toLowerCase().includes(inputValue.toLowerCase())
  );

  useEffect(() => {
    const externalCode = giftCardCode || '';
    if (externalCode !== lastExternalCodeRef.current) {
      lastExternalCodeRef.current = externalCode;
      setInputValue(externalCode);
    }
  }, [giftCardCode]);

  const handleInputChange = e => {
    const value = e.target.value;
    setInputValue(value);

    if (hasUserVouchers) {
      setShowSuggestions(true);
    }
  };

  const applyVoucherCode = () => {
    const value = (inputValue || '').trim();
    if (!value) {
      return;
    }
    if (onApplyVoucher) {
      onApplyVoucher(value);
    } else if (setGiftCardCode) {
      setGiftCardCode(value);
    }
  };

  const handleInputFocus = () => {
    if (hasUserVouchers) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleVoucherSelect = voucherCode => {
    setInputValue(voucherCode);
    lastExternalCodeRef.current = voucherCode;
    setShowSuggestions(false);
    inputRef.current?.blur();
  };

  const handleSuggestionPointerDown = (e, voucherCode) => {
    e.preventDefault();
    handleVoucherSelect(voucherCode);
  };

  const handleClearVoucher = () => {
    if (giftCardCode && onRemoveVoucher) {
      onRemoveVoucher();
    }
    setInputValue('');
    lastExternalCodeRef.current = '';
    if (!onRemoveVoucher && setGiftCardCode) {
      setGiftCardCode('');
    }
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleInputKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyVoucherCode();
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={css.voucherSection}>
      <Heading as="h3" rootClassName={css.voucherTitle}>
        <FormattedMessage id="UnifiedVoucherInput.title" defaultMessage="Voucher Code" />
      </Heading>

      <div className={css.autocompleteContainer}>
        <div className={css.inputWrapper}>
          <input
            ref={inputRef}
            type="text"
            id="voucher-code"
            className={css.voucherInput}
            placeholder={
              hasUserVouchers
                ? intl.formatMessage({
                    id: 'UnifiedVoucherInput.placeholderWithVouchers',
                    defaultMessage: 'Enter voucher code or click to see your vouchers...',
                  })
                : intl.formatMessage({
                    id: 'UnifiedVoucherInput.placeholder',
                    defaultMessage: 'Enter voucher code...',
                  })
            }
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            disabled={disabled || voucherInProgress}
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />

          {inputValue ? (
            <button
              type="button"
              className={css.clearButton}
              onClick={handleClearVoucher}
              disabled={disabled || voucherInProgress}
            >
              ✕
            </button>
          ) : null}

          {giftCardCode && giftCardCode === inputValue && !voucherInProgress ? (
            <span className={css.appliedIcon}>✓</span>
          ) : null}
        </div>

        {showSuggestions && hasUserVouchers ? (
          <div ref={suggestionsRef} className={css.suggestionsDropdown}>
            {fetchingUserVouchers ? (
              <div className={css.loading}>
                <FormattedMessage
                  id="UnifiedVoucherInput.loading"
                  defaultMessage="Loading your vouchers..."
                />
              </div>
            ) : filteredVouchers.length > 0 ? (
              <div className={css.suggestionsList}>
                <div className={css.suggestionsHeader}>
                  <FormattedMessage
                    id="UnifiedVoucherInput.yourVouchers"
                    defaultMessage="Your vouchers:"
                  />
                </div>
                {filteredVouchers.map(voucher => (
                  <button
                    key={voucher.code}
                    type="button"
                    className={css.suggestionOption}
                    onPointerDown={e => handleSuggestionPointerDown(e, voucher.code)}
                    disabled={disabled}
                  >
                    <div className={css.suggestionCode}>#{voucher.code}</div>
                    <div className={css.suggestionBalance}>${voucher.balance} remaining</div>
                  </button>
                ))}
              </div>
            ) : inputValue ? (
              <div className={css.noSuggestions}>
                <FormattedMessage
                  id="UnifiedVoucherInput.noMatches"
                  defaultMessage="No matching vouchers found. Continue typing to enter manually."
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className={css.applyButtonWrapper}>
        <PrimaryButton
          type="button"
          onClick={applyVoucherCode}
          disabled={!inputValue?.trim() || disabled || voucherInProgress}
        >
          <FormattedMessage id="UnifiedVoucherInput.applyButton" defaultMessage="Apply voucher" />
        </PrimaryButton>
      </div>

      {giftCardCode && !voucherInProgress ? (
        <div className={css.appliedVoucher}>
          <span className={css.appliedStatusIcon}>✓</span>
          <FormattedMessage
            id="UnifiedVoucherInput.appliedMessage"
            defaultMessage="Voucher #{code} applied"
            values={{ code: giftCardCode }}
          />
        </div>
      ) : null}

      {voucherInProgress ? (
        <div className={css.processingMessage}>
          <FormattedMessage
            id="CheckoutPage.updatingVoucherPricing"
            defaultMessage="Updating your total…"
          />
        </div>
      ) : null}

      <div className={css.helpText}>
        <FormattedMessage
          id="UnifiedVoucherInput.helpText"
          defaultMessage={
            hasUserVouchers
              ? 'Type any voucher code or click the field to see your available vouchers.'
              : 'Enter any voucher code.'
          }
        />
      </div>
    </div>
  );
};

export default UnifiedVoucherInput;
